import React, { useState, useEffect, useCallback } from "react";
import AdminCRUDPage from "../../components/admin/AdminCRUDPage";
import { educationAPI } from "../../services/api";
import toast from "react-hot-toast";

const EducationForm = ({ initialData, onSuccess }) => {
  const [form, setForm] = useState({
    institution: "", degree: "", fieldOfStudy: "", grade: "",
    startYear: new Date().getFullYear(), endYear: null, isCurrent: false,
    location: "", institutionLogo: "", description: "", subjects: [],
    order: 0, isPublished: true,
    ...(initialData || {}),
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubject = (i, val) => {
    const arr = [...(form.subjects || [])];
    arr[i] = val;
    set('subjects', arr);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (initialData?._id) {
        await educationAPI.update(initialData._id, payload);
        toast.success('Education updated!');
      } else {
        await educationAPI.create(payload);
        toast.success('Education created!');
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Institution *</label>
          <input
            className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
            value={form.institution || ""}
            onChange={(e) => set('institution', e.target.value)}
            placeholder="e.g. MIT"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Degree *</label>
          <input
            className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
            value={form.degree || ""}
            onChange={(e) => set('degree', e.target.value)}
            placeholder="e.g. Bachelor of Science"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Field of Study</label>
          <input
            className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
            value={form.fieldOfStudy || ""}
            onChange={(e) => set('fieldOfStudy', e.target.value)}
            placeholder="e.g. Computer Science"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Grade / GPA</label>
          <input
            className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
            value={form.grade || ""}
            onChange={(e) => set('grade', e.target.value)}
            placeholder="e.g. 3.9 / 4.0 or First Class"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Start Year *</label>
          <input
            type="number"
            min="1990"
            max="2099"
            className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
            value={form.startYear || ""}
            onChange={(e) => set('startYear', Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">End Year</label>
          <input
            type="number"
            min="1990"
            max="2099"
            className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
            value={form.endYear || ""}
            onChange={(e) => set('endYear', Number(e.target.value))}
            disabled={form.isCurrent}
          />
          <label className="flex items-center gap-2 mt-1 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isCurrent || false}
              onChange={(e) => { set('isCurrent', e.target.checked); if (e.target.checked) set('endYear', null); }}
              className="accent-primary-500"
            />
            Currently studying
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-1">Location</label>
        <input
          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
          value={form.location || ""}
          onChange={(e) => set('location', e.target.value)}
          placeholder="e.g. Cambridge, MA, USA"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-1">Institution Logo URL</label>
        <input
          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
          value={form.institutionLogo || ""}
          onChange={(e) => set('institutionLogo', e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea
          rows={3}
          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none resize-none"
          value={form.description || ""}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Brief description of your studies, notable projects, clubs..."
        />
      </div>

      {/* Subjects */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-gray-400">Key Subjects</label>
          <button
            type="button"
            onClick={() => set('subjects', [...(form.subjects || []), ""]) }
            className="text-xs text-primary-400 hover:text-primary-300"
          >+ Add</button>
        </div>
        {(form.subjects || []).map((s, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              className="flex-1 bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none text-sm"
              value={s}
              onChange={(e) => handleSubject(i, e.target.value)}
              placeholder={`Subject ${i + 1}`}
            />
            <button
              type="button"
              onClick={() => set('subjects', (form.subjects || []).filter((_, idx) => idx !== i))}
              className="text-red-400 hover:text-red-300 px-2"
            >✕</button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Display Order</label>
          <input
            type="number"
            className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-primary-500 outline-none"
            value={form.order ?? 0}
            onChange={(e) => set('order', Number(e.target.value))}
          />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isPublished ?? true}
              onChange={(e) => set('isPublished', e.target.checked)}
              className="accent-primary-500"
            />
            Published
          </label>
        </div>
      </div>

      <button type="submit" disabled={loading} className="btn-primary w-full py-3 disabled:opacity-50">
        {loading ? 'Saving...' : (initialData ? 'Update Education' : 'Create Education')}
      </button>
    </form>
  );
};

export default function AdminEducation() {
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await educationAPI.getAllAdmin?.() ?? await educationAPI.getAll();
      setEducations(res.data?.education ?? res.data?.educations ?? res.data?.data ?? []);
    } catch (err) {
      toast.error('Failed to load education');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    await educationAPI.delete(id);
    setEducations(p => p.filter(x => x._id !== id));
  };

  const columns = [
    { key: "institution", label: "Institution" },
    { key: "degree", label: "Degree" },
    { key: "fieldOfStudy", label: "Field" },
    {
      key: "startYear",
      label: "Period",
      render: (row) => `${row.startYear || "?"} – ${row.isCurrent ? "Present" : row.endYear || "?"}`,
    },
    {
      key: "isPublished",
      label: "Status",
      render: (row) => (
        <span className={`text-xs px-2 py-1 rounded-full ${row.isPublished ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}`}>
          {row.isPublished ? "Published" : "Draft"}
        </span>
      ),
    },
  ];

  const FormWithRefresh = (props) => (
    <EducationForm {...props} onSuccess={() => { props.onSuccess(); load(); }} />
  );

  return (
    <AdminCRUDPage
      title="Education"
      addButtonLabel="Add Education"
      items={educations}
      loading={loading}
      columns={columns}
      onDelete={handleDelete}
      FormComponent={FormWithRefresh}
      formTitle="Add Education"
    />
  );
}
